// The chips under each automation in the Control Room's Automations card.
// Each names the tool that automation really uses, as { tool, label }: `tool`
// is a ToolIcon key (components/ToolIcon.js) drawn as a small logo before the
// label, and a chip with no tool to show is { label } alone. The rules mirror
// the backend, so a chip never names a tool BossDrop would not use.
import { TASK_TOOL_ORDER } from "./taskTools";

// Short names ("Teams", not "Microsoft Teams"): the logo sits right beside
// them. monday.com keeps its own lower-case spelling.
export const CHIP_TOOL_NAMES = {
  teams: "Teams",
  slack: "Slack",
  email: "Email",
  google: "Google",
  outlook: "Outlook",
  planner: "Planner",
  asana: "Asana",
  jira: "Jira",
  linear: "Linear",
  clickup: "ClickUp",
  notion: "Notion",
  monday: "monday.com",
};

const CHAT_TOOLS = ["teams", "slack"];

const toolName = (tool) => CHIP_TOOL_NAMES[tool] || String(tool || "");

function pick(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

/**
 * The chat tool one automation uses: its own pick (dailyStatusChat,
 * escalationsChat, weeklyReportChat) when that tool is connected, otherwise
 * the workspace's chat tool. resolveChatBackend in
 * backend/server/automationWorker.js.
 */
export function chatToolFor(choice, connected = {}, workspaceTool = null) {
  const p = pick(choice);
  if (CHAT_TOOLS.includes(p) && connected[p]) return p;
  return workspaceTool || null;
}

/**
 * The task tool new tasks go to: the automation's own pick (autoTasksBackend)
 * when that tool is ready, otherwise the workspace's task tool.
 * resolveTaskBackend in backend/server/automationWorker.js.
 */
export function taskToolFor(choice, ready = {}, workspaceTool = null) {
  const p = pick(choice);
  if (TASK_TOOL_ORDER.includes(p) && ready[p]) return p;
  return workspaceTool || null;
}

// "auto" | "chat" | "email", like escalationDeliveryMode and
// weeklyDeliveryMode on the server.
function deliveryMode(value) {
  const v = pick(value) || "auto";
  return v === "email" || v === "chat" ? v : "auto";
}

const EMAIL = { tool: "email", label: "Delivery: Email" };
const EMAIL_FALLBACK = { tool: "email", label: "Email fallback" };
const CONNECT_CHAT = { label: "Connect Teams or Slack" };

/** Daily status pings: DMs on the chat tool. */
export function dailyPingChips(chat) {
  return [
    chat ? { tool: chat, label: `Chat: ${toolName(chat)}` } : CONNECT_CHAT,
    { label: "DM delivery" },
  ];
}

/** Auto-create & update tasks: new tasks land in the task tool. */
export function taskChips(task) {
  return [
    task
      ? { tool: task, label: `Tasks: ${toolName(task)}` }
      : { label: "Connect a task tool" },
    { label: "Create + update" },
    { label: "Dedup guard on" },
  ];
}

/**
 * Auto-escalations (sendEscalation in backend/server/automationWorker.js):
 * "email" sends email only; "chat" DMs on the chat tool and never emails;
 * "auto" DMs first and emails when the DM fails, which with no chat tool
 * connected means email every time.
 */
export function escalationChips({ delivery, chat } = {}) {
  const mode = deliveryMode(delivery);
  const format = { label: "Escalation format" };
  if (mode === "email") return [EMAIL, format];
  if (!chat) return [mode === "chat" ? CONNECT_CHAT : EMAIL, format];
  const name = toolName(chat);
  if (mode === "chat") {
    return [{ tool: chat, label: `Delivery: ${name} only` }, format];
  }
  return [{ tool: chat, label: `Delivery: ${name}` }, EMAIL_FALLBACK, format];
}

/**
 * Executive report delivery (send_weekly_report in
 * backend/server/brain/executePlan.js). It DMs only the exec recipient, so
 * without one (or without a chat tool) it is email alone. With both, "auto"
 * emails AND DMs, and "chat" DMs and emails only when the DM fails.
 */
export function reportDeliveryChips({ delivery, chat, dmRecipient } = {}) {
  const mode = deliveryMode(delivery);
  if (mode === "email" || !chat || !dmRecipient) return [EMAIL];
  const name = toolName(chat);
  if (mode === "chat") {
    return [{ tool: chat, label: `Delivery: ${name} DM` }, EMAIL_FALLBACK];
  }
  return [EMAIL, { tool: chat, label: `Plus a ${name} DM` }];
}

/**
 * Auto-create meetings. They are booked on the workspace owner's calendar
 * (createMeeting in backend/server/autoScheduleMeetings/index.js): Google first
 * with Outlook as the fallback, or Outlook first with Google as the fallback
 * when settings.calendarWriteBackend is "microsoft". `calendars` is
 * { status: "ready", google, outlook } once read; any other status means it
 * is not known (still loading, or this viewer may not read the owner's).
 * `preferred` is settings.calendarWriteBackend: "microsoft", or "google" /
 * absent for the default.
 */
export function meetingChips(calendars, preferred = null) {
  const freeBusy = { label: "Free/busy based" };
  if (calendars?.status !== "ready") {
    return [{ label: "Owner's calendar" }, freeBusy];
  }
  const google = !!calendars.google;
  const outlook = !!calendars.outlook;
  const outlookFirst = pick(preferred) === "microsoft";
  const GOOGLE = { tool: "google", label: "Calendar: Google" };
  const OUTLOOK = { tool: "outlook", label: "Calendar: Outlook" };
  if (google && outlook) {
    return outlookFirst
      ? [OUTLOOK, { tool: "google", label: "Google fallback" }, freeBusy]
      : [GOOGLE, { tool: "outlook", label: "Outlook fallback" }, freeBusy];
  }
  if (google) return [GOOGLE, freeBusy];
  if (outlook) return [OUTLOOK, freeBusy];
  return [{ label: "Connect a calendar" }, freeBusy];
}
